import { widget } from '../../widgets/widget-decorator';
import { color, create, percent, unuseTheme, useTheme } from '@amcharts/amcharts4/core';
import { XYChart, ColumnSeries, CategoryAxis, ValueAxis } from '@amcharts/amcharts4/charts';
import { BindingEngine } from 'aurelia-binding';
import { PLATFORM } from 'aurelia-pal';
import { customElement, useView } from 'aurelia-templating';
import { ChartWidgetBase } from '../chart-widget-base';
import { CustomerService } from '../../services/customer-service';
import { IApplicationFilter } from '../../interfaces/i-application-filter';
import { BalanceReportViewModel, GlReportsClient } from '../../clients/reporting-clients';
import { getChartReadyPromise } from '../../services/chart-ready';
import { CacheService } from '../../services/cache-service';
import { property } from '../property-decorator';

@customElement('nav-breakdown-widget')
@widget('NAV breakdown', true, true)
@useView(PLATFORM.moduleName('./nav-breakdown-widget.html'))
export class NavBreakdownWidget extends ChartWidgetBase {
  constructor(element: Element, customerService: CustomerService, private glReportsClient: GlReportsClient, bindingEngine: BindingEngine, private cacheService: CacheService) {
    super(element, bindingEngine, customerService);
  }

  chartObject: XYChart;

  @property('Style')
  chartTheme: string;

  @property('Style')
  labelColour?: string;

  override async loadData(filter: IApplicationFilter) {
    return this.cacheService.getOrSet(`nav-breakdown-widget-${filter.customer.companyId}-${filter.dates.toStr}`,
      () => this.glReportsClient.balance(filter.customer.companyId, filter.dates.toStr),
      60000, false);
  }

  override async setData(data: BalanceReportViewModel) {
    const dataArray = [
      { category: 'Value', amount: data.keyPortfolioValue },
      { category: 'Bank', amount: data.keyBank },
      { category: 'Other', amount: data.keyOther },
      { category: 'Debt', amount: data.keyDebt }
    ];
    this.chartObject?.dispose();
    const theme = this.customerService.getChartTheme(this.chartTheme);
    useTheme(theme);
    this.chartObject = create(this.chart, XYChart);
    const readyPromise = getChartReadyPromise(this.chartObject);
    this.chartObject.height = percent(100);
    this.chartObject.hiddenState.properties.opacity = 0;
    this.chartObject.fontSize = 9;

    const labelColour = color(this.getChartLabelColour(this.labelColour));
    const categoryAxis = this.chartObject.xAxes.push(new CategoryAxis());
    categoryAxis.dataFields.category = 'category';
    categoryAxis.renderer.labels.template.fill = labelColour;
    categoryAxis.renderer.minGridDistance = 10;
    categoryAxis.renderer.inside = false;

    const yAxis = this.chartObject.yAxes.push(new ValueAxis());
    yAxis.numberFormatter.numberFormat = '#a';
    yAxis.renderer.labels.template.fill = labelColour;
    [yAxis.min, yAxis.max] = dataArray.reduce((p, c) => {
      p[0] = Math.min(p[0], c.amount);
      p[1] = Math.max(p[1], c.amount);
      return p;
    }, [dataArray[0].amount, dataArray[0].amount]);
    yAxis.strictMinMax = true;

    const colSeries = this.chartObject.series.push(new ColumnSeries());
    colSeries.dataFields.valueY = 'amount';
    colSeries.dataFields.categoryX = 'category';
    colSeries.columns.template.stroke = color('#343a40');
    colSeries.columns.template.strokeWidth = 2;
    colSeries.columns.template.strokeOpacity = 1;
    colSeries.numberFormatter.numberFormat = '#a';
    colSeries.columns.template.tooltipText = '{category} {valueY}';
    // Add distinctive colors for each column using adapter
    colSeries.columns.template.adapter.add('fill', (fill, target) => {
      return this.chartObject.colors.getIndex(target.dataItem.index);
    });
    unuseTheme(theme);
    this.chartObject.data = dataArray;
    await readyPromise;
  }

  override configChanged(key: keyof NavBreakdownWidget) {
    switch (key) {
      case 'labelColour':
        const labelColor = color(this.getChartLabelColour(this.labelColour));
        this.chartObject.xAxes.each(x => x.renderer.labels.template.fill = x.renderer.ticks.template.stroke = labelColor);
        this.chartObject.yAxes.each(x => x.renderer.labels.template.fill = x.renderer.ticks.template.stroke = labelColor);
        break;
      default:
        this.refresh$.next();
        break;
    }
  }

}
